import { useState, useEffect } from 'react'
import CalendarExport from './CalendarExport'
import { supabase } from '../supabase'

// ─── WEEKLY MANTRAS ─────────────────────────────────────────────────
const MANTRAS = [
  "I am building something that works. Most job seekers wing it. I am not most job seekers.",
  "One warm conversation is worth forty cold applications. Today I make the call.",
  "The pipeline never sleeps. I keep feeding it even when things feel promising.",
  "I know things about this process that most candidates don't. That knowledge is my advantage.",
  "Every no is closer to my yes. The system is working even when it doesn't feel like it.",
  "The candidates who find jobs are not the most qualified. They are the most consistent. I am consistent.",
  "I do not cross the finish line in my head before I cross it in reality. Keep the pipeline moving.",
  "I evaluate every opportunity like a recruiter would. Thoroughly, honestly, without desperation.",
  "The system works on its own timeline. Keep going.",
  "Consistency is the only variable still in my control.",
  "Every conversation I have this week is one closer to the yes.",
  "I have built something real. The offer is coming. Keep going.",
]

function getMantra(activeDays) {
  const week = Math.floor(activeDays / 7)
  return MANTRAS[Math.min(week, MANTRAS.length - 1)]
}

// ─── PLACEHOLDER AUTO-FILL ────────────────────────────────────────────
// Replaces the brackets we can confidently fill from onboarding data.
// Anything we're not sure about (company names, dollar amounts, dates)
// is left as-is so the person still knows to fill it in by hand.
function fillPlaceholders(text, profile) {
  if (!text || !profile) return text
  const title = (profile.target_titles || '').split(',')[0]?.trim()
  const replacements = {
    '[YOUR INDUSTRY]': profile.industry,
    '[YOUR CITY]': profile.city,
    '[YOUR GEOGRAPHY]': profile.city,
    '[YOUR GEOGRAPHY OR REMOTE]': profile.city,
    '[YOUR FIELD AND GEOGRAPHY]': profile.industry && profile.city ? `${profile.industry} in ${profile.city}` : null,
    '[YOUR TARGET TITLE]': title,
    '[YOUR PROFESSION]': title,
    '[YOUR SPECIFIC ROLE]': title,
    '[YOUR TITLE]': title,
  }
  let result = text
  for (const [token, value] of Object.entries(replacements)) {
    if (value) result = result.split(token).join(value)
  }
  return result
}

// ─── AI PROMPT BLOCK ─────────────────────────────────────────────────
// Now includes a save-back field: what did Claude actually give you?
// Persisted to daily_responses, keyed by user + day + prompt index.
function PromptBlock({ label, prompt, session, day, promptIndex }) {
  const [copied, setCopied] = useState(false)
  const [response, setResponse] = useState('')
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!session?.user?.id || day == null || promptIndex == null) { setLoaded(true); return }
      const { data } = await supabase
        .from('daily_responses')
        .select('content, updated_at')
        .eq('user_id', session.user.id)
        .eq('day_number', day)
        .eq('prompt_index', promptIndex)
        .maybeSingle()
      if (!cancelled && data) {
        setResponse(data.content || '')
        setSavedAt(data.updated_at)
      }
      if (!cancelled) setLoaded(true)
    }
    load()
    return () => { cancelled = true }
  }, [session?.user?.id, day, promptIndex])

  function copy() {
    navigator.clipboard.writeText(prompt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function save() {
    if (!session?.user?.id || day == null || promptIndex == null) return
    setSaving(true)
    const { error } = await supabase.from('daily_responses').upsert({
      user_id: session.user.id,
      day_number: day,
      prompt_index: promptIndex,
      content: response,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,day_number,prompt_index' })
    if (!error) setSavedAt(new Date().toISOString())
    setSaving(false)
  }

  return (
    <div style={{ background: '#1A1A1A', borderRadius: '8px', padding: '1rem', marginBottom: '0.85rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>
        💡 {label}
      </div>
      <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.82rem', lineHeight: 1.6, marginBottom: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {prompt}
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: response || !loaded ? '0.75rem' : 0 }}>
        <button onClick={copy} style={{ padding: '0.5rem 0.85rem', background: copied ? '#00712D' : '#fff', color: copied ? '#fff' : '#1A1A1A', border: 'none', borderRadius: '5px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', flex: 1 }}>
          {copied ? '✓ Copied!' : 'Copy prompt'}
        </button>
        <a href="https://claude.ai" target="_blank" rel="noopener noreferrer" style={{ padding: '0.5rem 0.85rem', background: '#00712D', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          Open Claude.ai →
        </a>
      </div>

      {day != null && promptIndex != null && (
        <div>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>
            Paste what Claude gave you
          </div>
          <textarea
            value={response}
            onChange={e => setResponse(e.target.value)}
            onBlur={save}
            placeholder="Paste Claude's response here so it's saved to your search..."
            rows={4}
            style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.75rem', fontSize: '0.82rem', color: '#fff', fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical', outline: 'none' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.4rem' }}>
            <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)' }}>
              {saving ? 'Saving...' : savedAt ? '✓ Saved' : ''}
            </span>
            <button onClick={save} disabled={saving} style={{ padding: '0.35rem 0.75rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', borderRadius: '5px', fontSize: '0.72rem', cursor: 'pointer' }}>
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── STEP NOTE ─────────────────────────────────────────────────────
// A collapsible write-in field attached to any checklist step —
// not just AI-prompt steps — so real progress leaves a real record.
function StepNote({ value, onSave }) {
  const [open, setOpen] = useState(!!value)
  const [text, setText] = useState(value)
  const [saving, setSaving] = useState(false)

  async function save() {
    if (text === value) return
    setSaving(true)
    await onSave(text)
    setSaving(false)
  }

  if (!open) {
    return (
      <div style={{ padding: '0 0.8rem 0.6rem' }}>
        <button onClick={() => setOpen(true)} style={{ background: 'none', border: 'none', color: '#00712D', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
          + Add a note
        </button>
      </div>
    )
  }

  return (
    <div style={{ padding: '0 0.8rem 0.8rem' }}>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={save}
        placeholder="What actually happened? Who did you talk to, what did they say..."
        rows={2}
        style={{ width: '100%', border: '1px solid #e8e8e8', borderRadius: '5px', padding: '0.5rem 0.65rem', fontSize: '0.8rem', color: '#1A1A1A', fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical', outline: 'none', background: '#fff' }}
      />
      {saving && <div style={{ fontSize: '0.65rem', color: '#aaa', marginTop: '0.2rem' }}>Saving...</div>}
    </div>
  )
}


function LessonCard({ lesson }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div style={{ background: '#E8F2EC', borderRadius: '10px', marginBottom: '1rem', overflow: 'hidden' }}>
      <button onClick={() => setExpanded(!expanded)} style={{ width: '100%', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', gap: '0.75rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#C9A84C', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
            📖 Today's Lesson · {lesson.readTime} · {lesson.chapter}
          </div>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>{lesson.title}</div>
          {!expanded && <div style={{ fontSize: '0.78rem', color: '#555', marginTop: '0.2rem', lineHeight: 1.4 }}>{lesson.preview}</div>}
        </div>
        <div style={{ color: '#00712D', fontWeight: 700, fontSize: '1.1rem', flexShrink: 0 }}>{expanded ? '↑' : '↓'}</div>
      </button>
      {expanded && (
        <div style={{ padding: '0 1rem 1rem', borderTop: '1px solid rgba(0,113,45,0.15)' }}>
          <div style={{ paddingTop: '0.75rem', fontSize: '0.875rem', color: '#1A1A1A', lineHeight: 1.75, whiteSpace: 'pre-line' }}>{lesson.content}</div>
          <div style={{ marginTop: '0.75rem', fontSize: '0.72rem', color: '#00712D', fontWeight: 600 }}>↳ Read more in {lesson.chapter} of The Application Multiplier</div>
        </div>
      )}
    </div>
  )
}

// ─── SAVE BUTTON ─────────────────────────────────────────────────────
function SaveButton({ onSave, saving, saved, disabled }) {
  return (
    <button className="btn-primary" onClick={onSave} disabled={saving || disabled} style={{ width: '100%', padding: '0.9rem', marginTop: '1rem' }}>
      {saving ? 'Saving...' : saved ? '✓ Saved to My Search' : 'Save to My Search →'}
    </button>
  )
}

// ─── INLINE FIELD ────────────────────────────────────────────────────
function Field({ label, hint, value, onChange, type = 'textarea', rows = 4, placeholder, options }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1A1A1A', display: 'block', marginBottom: '0.25rem' }}>{label}</label>
      {hint && <p style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.5rem', lineHeight: 1.5 }}>{hint}</p>}
      {type === 'textarea' && <textarea className="input-field" rows={rows} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} style={{ resize: 'vertical', fontSize: '0.875rem' }} />}
      {type === 'text' && <input className="input-field" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} style={{ fontSize: '0.875rem' }} />}
      {type === 'choice' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {options.map(opt => (
            <button key={opt} onClick={() => onChange(opt)} style={{ padding: '0.65rem 0.85rem', border: `2px solid ${value === opt ? '#00712D' : '#e8e8e8'}`, borderRadius: '6px', background: value === opt ? '#E8F2EC' : '#fff', color: value === opt ? '#00712D' : '#555', textAlign: 'left', fontSize: '0.85rem', fontWeight: value === opt ? 600 : 400, cursor: 'pointer' }}>
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── DAY 1 — IKIGAI ─────────────────────────────────────────────────
function Day1Exercise({ session, profile, onComplete }) {
  const [data, setData] = useState({ love: profile?.what_you_love || '', good_at: profile?.what_youre_good_at || '', world_needs: profile?.what_world_needs || '', start_stop_keep: profile?.start_stop_keep || '' })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [showColdApplyStats, setShowColdApplyStats] = useState(!profile?.what_you_love)

  async function save() {
    setSaving(true)
    await supabase.from('profiles').update({ what_you_love: data.love, what_youre_good_at: data.good_at, what_world_needs: data.world_needs, start_stop_keep: data.start_stop_keep }).eq('id', session.user.id)
    setSaving(false); setSaved(true)
    setTimeout(() => onComplete(), 1200)
  }

  const filled = data.love.trim() && data.good_at.trim() && data.world_needs.trim()

  if (showColdApplyStats) return (
    <div style={{ animation: 'slideUp 0.4s ease' }}>
      <div style={{ background: '#1A1A1A', borderRadius: '12px', padding: '1.5rem', marginBottom: '1rem', textAlign: 'center' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#C9A84C', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1rem' }}>Before we start — here's what you're up against</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
          {[
            { stat: '250+', label: 'Average applications per job posting' },
            { stat: '0.5%', label: 'Cold application hire rate' },
            { stat: '7%', label: 'Of applications come from referrals' },
            { stat: '40%', label: 'Of hires come from referrals' },
          ].map(s => (
            <div key={s.label} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '8px', padding: '1rem' }}>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.75rem', fontWeight: 700, color: '#C9A84C', lineHeight: 1 }}>{s.stat}</div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.3rem', lineHeight: 1.4 }}>{s.label}</div>
            </div>
          ))}
        </div>
        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem', lineHeight: 1.65, marginBottom: '0.75rem' }}>
          Most job seekers are playing a game with a 0.5% win rate. Referrals produce 40% of hires from just 7% of applications. One referral is worth more than eighty cold applications.
        </p>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', lineHeight: 1.65 }}>
          This is not a resume problem. This is a strategy problem. The next 60 days are the strategy.
        </p>
      </div>
      <button className="btn-primary" onClick={() => setShowColdApplyStats(false)} style={{ width: '100%', padding: '0.9rem', fontSize: '0.95rem' }}>
        I'm ready. Let's build it. →
      </button>
    </div>
  )

  return (
    <div style={{ animation: 'slideUp 0.3s ease' }}>
      <div style={{ background: '#E8F2EC', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#1A1A1A', lineHeight: 1.6 }}>
        <strong>The Ikigai framework</strong> sits at the intersection of what you love, what you're good at, what the world needs, and what you can be paid for. Two to three hours of clarity here will save you two months of wandering. Your answers become the foundation of every cover letter, every networking conversation, and every answer to "tell me about yourself."
      </div>

      <Field label="What energizes you at work?" hint="What work makes time disappear? What would you do even if you weren't paid for it? Be specific — not 'helping people' but how specifically you help and why it matters to you." value={data.love} onChange={v => setData({ ...data, love: v })} placeholder="Think about your best days at work. What were you doing? What made those days feel meaningful?" rows={5} />

      <Field label="What are you genuinely better at than most?" hint="Not what you've done — what you're actually excellent at. Think about the skills, abilities, and strengths that colleagues have consistently recognized in you." value={data.good_at} onChange={v => setData({ ...data, good_at: v })} placeholder="What do people come to you for? What do you do that others find difficult? What comes naturally to you that doesn't come naturally to others?" rows={5} />

      <Field label="What impact do you want your work to have?" hint="What problems are worth solving? What value is worth creating? What would make you feel like the work matters beyond the paycheck?" value={data.world_needs} onChange={v => setData({ ...data, world_needs: v })} placeholder="Think about the work you've been most proud of. What difference did it make? Who did it help? What would the world lose if that work didn't exist?" rows={5} />

      <Field label="Start / Stop / Keep" hint="Three honest lists from your career so far." value={data.start_stop_keep} onChange={v => setData({ ...data, start_stop_keep: v })} placeholder={`START: Things you want more of in your next role — activities, responsibilities, types of work\n\nSTOP: Things you never want to do again — what drains you, what doesn't fit\n\nKEEP: What's worth carrying forward from your current or last role`} rows={8} />

      <SaveButton onSave={save} saving={saving} saved={saved} disabled={!filled} />
      {!filled && <p style={{ fontSize: '0.75rem', color: '#aaa', textAlign: 'center', marginTop: '0.5rem' }}>Fill in the first three fields to save</p>}
    </div>
  )
}

// ─── DAY 2 — JOB SPEC ───────────────────────────────────────────────
function Day2Exercise({ session, profile, onComplete }) {
  const [data, setData] = useState({
    target_titles: profile?.target_titles || '',
    ideal_salary: profile?.ideal_salary || '',
    minimum_salary: profile?.minimum_salary || '',
    work_format: profile?.work_format || '',
    company_size: profile?.company_size || '',
    non_negotiables: profile?.non_negotiables || '',
    deal_breakers: profile?.deal_breakers || '',
    nice_to_haves: profile?.nice_to_haves || '',
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  async function save() {
    setSaving(true)
    await supabase.from('profiles').update({ ...data }).eq('id', session.user.id)
    setSaving(false); setSaved(true)
    setTimeout(() => onComplete(), 1200)
  }

  const filled = data.target_titles.trim() && data.minimum_salary.trim()

  return (
    <div style={{ animation: 'slideUp 0.3s ease' }}>
      <div style={{ background: '#E8F2EC', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#1A1A1A', lineHeight: 1.6 }}>
        <strong>Your Personal Job Spec</strong> is the filter every opportunity gets run through. Without it you'll apply to roles that pay less than you need, require a commute you can't do, or ask you to do work you've already decided you don't want. It takes 20 minutes to build and saves weeks of wasted effort.
      </div>

      <Field label="Target job titles *" hint="Be specific. List up to 5 titles you'd genuinely accept." value={data.target_titles} onChange={v => setData({ ...data, target_titles: v })} placeholder="e.g. Director of HR, VP of People, CHRO" type="text" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <Field label="Ideal base salary" hint="Your goal." value={data.ideal_salary} onChange={v => setData({ ...data, ideal_salary: v })} placeholder="e.g. $130,000" type="text" />
        <Field label="Minimum salary *" hint="Your floor. Non-negotiable." value={data.minimum_salary} onChange={v => setData({ ...data, minimum_salary: v })} placeholder="e.g. $110,000" type="text" />
      </div>

      <Field label="Work format" value={data.work_format} onChange={v => setData({ ...data, work_format: v })} type="choice" options={['Fully remote', 'Hybrid — 1-2 days in office', 'Hybrid — 3-4 days in office', 'Fully in-person', 'Open to any']} />

      <Field label="Company size preference" hint="What environment do you do your best work in?" value={data.company_size} onChange={v => setData({ ...data, company_size: v })} placeholder="e.g. Series B startup to 500 people, or established company 1,000+" type="text" />

      <Field label="Non-negotiables" hint="Things you will not compromise on regardless of the offer." value={data.non_negotiables} onChange={v => setData({ ...data, non_negotiables: v })} placeholder="e.g. Remote flexibility, strong direct manager, no travel over 20%, values alignment on X" rows={3} />

      <Field label="Deal-breakers" hint="Things that end a process immediately." value={data.deal_breakers} onChange={v => setData({ ...data, deal_breakers: v })} placeholder="e.g. Below salary minimum, requires relocation, toxic culture signals, poorly funded" rows={3} />

      <Field label="Nice to haves" hint="Important but not required." value={data.nice_to_haves} onChange={v => setData({ ...data, nice_to_haves: v })} placeholder="e.g. Equity, strong L&D budget, large team, brand recognition" rows={3} />

      <SaveButton onSave={save} saving={saving} saved={saved} disabled={!filled} />
      {!filled && <p style={{ fontSize: '0.75rem', color: '#aaa', textAlign: 'center', marginTop: '0.5rem' }}>Target titles and minimum salary are required</p>}
    </div>
  )
}

// ─── DAY 3 — LINKEDIN ───────────────────────────────────────────────
function Day3Exercise({ session, profile, onComplete }) {
  const [headline, setHeadline] = useState('')
  const [about, setAbout] = useState('')
  const [aiHeadline, setAiHeadline] = useState('')
  const [aiAbout, setAiAbout] = useState('')
  const [loading, setLoading] = useState(false)
  const [checklist, setChecklist] = useState([])
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const isEmployed = profile?.situation === 'employed'

  const LINKEDIN_ITEMS = [
    isEmployed ? 'Turned off "Share profile updates with your network" BEFORE making changes' : 'Professional photo added or updated',
    'Headline updated with target title + credential + specialty',
    'About section rewritten in first person',
    'All 50 skills updated for target roles',
    isEmployed ? 'Open to Work set to RECRUITERS ONLY (not public)' : 'Open to Work turned on for recruiters only',
    'Custom LinkedIn URL set',
    '3+ connection requests sent to former colleagues',
    isEmployed ? null : '3 LinkedIn recommendations requested',
  ].filter(Boolean)

  async function save() {
    setSaving(true)
    await supabase.from('profiles').update({ linkedin_headline: headline, linkedin_about: about }).eq('id', session.user.id)
    setSaving(false); setSaved(true)
    setTimeout(() => onComplete(), 1200)
  }

  const PROMPT = `I am targeting ${profile?.target_titles || 'my next role'}. Please rewrite my LinkedIn headline and About section to be keyword-optimized for recruiters searching for this role. Keep my authentic voice. My current headline is: "${headline}". My current About section is: "${about}".`

  return (
    <div style={{ animation: 'slideUp 0.3s ease' }}>
      {isEmployed && (
        <div style={{ background: '#fef3c7', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#92400e', lineHeight: 1.6 }}>
          🤫 <strong>Stealth note:</strong> Before making any LinkedIn changes, go to Settings → Visibility → turn OFF "Share profile updates with your network." This prevents your employer from seeing your edits. Turn it back on after.
        </div>
      )}

      <div style={{ background: '#E8F2EC', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#1A1A1A', lineHeight: 1.6 }}>
        Sourced candidates — people recruiters find rather than people who apply cold — are 5 times more likely to be hired. Your LinkedIn profile is bait. Today you make yourself findable.
      </div>

      <Field label="Your current LinkedIn headline" value={headline} onChange={setHeadline} type="text" placeholder="Paste your current headline here" />
      <Field label="Your current About section" value={about} onChange={setAbout} placeholder="Paste your current About section here — or leave blank if you don't have one" rows={5} />

      <PromptBlock label="Copy this prompt → paste into Claude.ai or ChatGPT" prompt={PROMPT} />



      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#555', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>LinkedIn checklist</div>
        {LINKEDIN_ITEMS.map((item, i) => {
          const done = checklist.includes(i)
          return (
            <div key={i} onClick={() => setChecklist(prev => done ? prev.filter(x => x !== i) : [...prev, i])} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0', borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '4px', border: `2px solid ${done ? '#00712D' : '#ccc'}`, background: done ? '#00712D' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {done && <span style={{ color: '#fff', fontSize: '0.65rem', fontWeight: 700 }}>✓</span>}
              </div>
              <span style={{ fontSize: '0.82rem', color: done ? '#888' : '#1A1A1A', textDecoration: done ? 'line-through' : 'none' }}>{item}</span>
            </div>
          )
        })}
      </div>

      <SaveButton onSave={save} saving={saving} saved={saved} disabled={false} />
    </div>
  )
}

// ─── DAY 4 — RESUME ─────────────────────────────────────────────────
function Day4Exercise({ session, profile, onComplete }) {
  const [resume, setResume] = useState('')
  const [jobDesc, setJobDesc] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const PROMPT = `I am applying for a role as ${profile?.target_titles?.split(',')[0] || 'my target role'}. Please analyze my resume against this job description. Tell me: (1) what keywords am I missing, (2) what's my ATS match score, (3) rewrite my top 3 bullet points to better match. Here is my resume: [PASTE RESUME]. Here is the job description: [PASTE JOB DESCRIPTION].`

  async function save() {
    setSaving(true)
    setSaving(false)
    setSaved(true)
    setTimeout(() => onComplete(), 1200)
  }

  return (
    <div style={{ animation: 'slideUp 0.3s ease' }}>
      <div style={{ background: '#E8F2EC', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#1A1A1A', lineHeight: 1.6 }}>
        Your resume is not being read by a human first. At most large companies it goes through software that parses and filters before a recruiter sees it. Today you make sure your resume earns its way past the filter.
      </div>

      <Field label="Paste your resume" value={resume} onChange={setResume} placeholder="Paste your full resume text here..." rows={6} />
      <Field label="Paste a target job description" value={jobDesc} onChange={setJobDesc} placeholder="Paste the job description you want to match..." rows={5} />

      <PromptBlock label="Copy this prompt → paste into ChatGPT or Claude.ai" prompt={PROMPT} />

      <SaveButton onSave={save} saving={saving} saved={saved} disabled={!resume || !jobDesc} />
      {(!resume || !jobDesc) && <p style={{ fontSize: '0.75rem', color: '#aaa', textAlign: 'center', marginTop: '0.5rem' }}>Paste both your resume and a job description to continue</p>}
    </div>
  )
}

// ─── DAY 5 — HOT TEN ─────────────────────────────────────────────────
function Day5Exercise({ session, profile, onComplete, onStatsUpdate }) {
  const [contacts, setContacts] = useState([])
  const [form, setForm] = useState({ name: '', company: '', title: '', relationship: '', contact_method: 'Phone' })
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [saved, setSaved] = useState(false)
  const isEmployed = profile?.situation === 'employed'

  useEffect(() => { loadContacts() }, [])

  async function loadContacts() {
    const { data } = await supabase.from('hot_ten').select('*').eq('user_id', session.user.id).order('created_at')
    setContacts(data || [])
    setLoading(false)
  }

  async function addContact() {
    if (!form.name.trim()) return
    setAdding(true)
    await supabase.from('hot_ten').insert({ user_id: session.user.id, ...form, status: 'not_yet' })
    setForm({ name: '', company: '', title: '', relationship: '', contact_method: 'Phone' })
    await loadContacts()
    if (onStatsUpdate) onStatsUpdate()
    setAdding(false)
    setSaved(true)
  }

  async function removeContact(id) {
    await supabase.from('hot_ten').delete().eq('id', id)
    await loadContacts()
    if (onStatsUpdate) onStatsUpdate()
  }

  return (
    <div style={{ animation: 'slideUp 0.3s ease' }}>
      <div style={{ background: '#E8F2EC', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#1A1A1A', lineHeight: 1.6 }}>
        <strong>The criteria:</strong> They know your work well, they want you to succeed, and they have enough professional reach to open doors. Think former managers, mentors, respected colleagues, clients who valued your work.
        {isEmployed && <><br /><br />🤫 <strong>Stealth note:</strong> Focus on people outside your current company first. Do not include anyone who might mention your search to your employer.</>}
      </div>

      {/* Progress */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#888', marginBottom: '0.3rem' }}>
          <span>{contacts.length}/10 contacts added</span>
          {contacts.length === 10 && <span style={{ color: '#00712D', fontWeight: 700 }}>✓ Complete!</span>}
        </div>
        <div style={{ height: '6px', background: '#e8e8e8', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${(contacts.length / 10) * 100}%`, background: '#00712D', transition: 'width 0.3s' }} />
        </div>
      </div>

      {/* Existing contacts */}
      {!loading && contacts.map((c, i) => (
        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.6rem', border: '1px solid #e8e8e8', borderRadius: '7px', marginBottom: '0.4rem', background: '#f9f9f9' }}>
          <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#00712D', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1A1A1A' }}>{c.name}</div>
            {c.company && <div style={{ fontSize: '0.72rem', color: '#888' }}>{c.title ? `${c.title} · ` : ''}{c.company}</div>}
          </div>
          <button onClick={() => removeContact(c.id)} style={{ fontSize: '0.72rem', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      ))}

      {/* Add form */}
      {contacts.length < 10 && (
        <div className="card" style={{ marginTop: '0.75rem' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#555', marginBottom: '0.75rem' }}>Add contact {contacts.length + 1}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <input className="input-field" placeholder="Full name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={{ fontSize: '0.875rem' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <input className="input-field" placeholder="Company" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} style={{ fontSize: '0.875rem' }} />
              <input className="input-field" placeholder="Their title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} style={{ fontSize: '0.875rem' }} />
            </div>
            <input className="input-field" placeholder="Your relationship (e.g. my manager at Acme 2021-2023)" value={form.relationship} onChange={e => setForm({ ...form, relationship: e.target.value })} style={{ fontSize: '0.875rem' }} />
            <select className="input-field" value={form.contact_method} onChange={e => setForm({ ...form, contact_method: e.target.value })} style={{ fontSize: '0.875rem' }}>
              {['Phone', 'LinkedIn', 'Email', 'Text'].map(m => <option key={m}>{m}</option>)}
            </select>
            <button className="btn-primary" onClick={addContact} disabled={adding || !form.name.trim()} style={{ width: '100%' }}>
              {adding ? 'Adding...' : '+ Add to Hot Ten'}
            </button>
          </div>
        </div>
      )}

      {contacts.length >= 1 && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ padding: '0.75rem', background: '#E8F2EC', borderRadius: '8px', fontSize: '0.82rem', color: '#00712D', fontWeight: 600, textAlign: 'center', marginBottom: '0.75rem' }}>
            ✓ Contacts saved to My Search automatically
          </div>
          <button className="btn-primary" onClick={onComplete} style={{ width: '100%', padding: '0.85rem' }}>
            {contacts.length >= 10 ? 'Hot Ten complete →' : `Continue with ${contacts.length} contacts — add more anytime →`}
          </button>
          {contacts.length < 10 && (
            <p style={{ fontSize: '0.72rem', color: '#888', textAlign: 'center', marginTop: '0.5rem' }}>
              You can add more contacts anytime from the Hot Ten tab
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── DAY 8 — TARGET COMPANIES ────────────────────────────────────────
function Day8Exercise({ session, profile, onComplete }) {
  const [companies, setCompanies] = useState(profile?.target_companies || '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  async function save() {
    setSaving(true)
    await supabase.from('profiles').update({ target_companies: companies }).eq('id', session.user.id)
    setSaving(false); setSaved(true)
    setTimeout(() => onComplete(), 1200)
  }

  const isEmployed = profile?.situation === 'employed'
  const targetCount = isEmployed ? '10' : '25'

  const PROMPT = `Give me ${targetCount} companies in [YOUR INDUSTRY] in [YOUR GEOGRAPHY OR REMOTE] that hire ${profile?.target_titles?.split(',')[0] || '[YOUR TARGET TITLE]'}. For each company include: company size, recent growth signals or funding, one reason it's worth targeting, and whether they're currently hiring. Format as a numbered list.`

  return (
    <div style={{ animation: 'slideUp 0.3s ease' }}>
      <div style={{ background: '#E8F2EC', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#1A1A1A', lineHeight: 1.6 }}>
        Between 24 and 32% of positions are filled before they're ever publicly posted. The candidates who hear about them first have a relationship inside the company. Your target company list is how you build those relationships before you need them.
        {isEmployed && <><br /><br />🤫 <strong>Stealth note:</strong> Space out your LinkedIn follows of these companies over a week — don't follow all {targetCount} at once.</>}
      </div>

      <PromptBlock
        label={`Step 1 — Use this prompt to build your list of ${targetCount}`}
        prompt={PROMPT}
       
      />

      <Field
        label={`Step 2 — Paste your ${targetCount} target companies here`}
        hint="Paste the results from your AI research. You can clean it up or just paste it raw — it's your reference document."
        value={companies}
        onChange={setCompanies}
        placeholder={`Paste your list of ${targetCount} target companies here...`}
        rows={10}
      />

      <SaveButton onSave={save} saving={saving} saved={saved} disabled={!companies.trim()} />
    </div>
  )
}

// ─── GENERIC ACTION DAY ──────────────────────────────────────────────
function ActionDay({ day, task, profile, session, onComplete }) {
  const [completedSteps, setCompletedSteps] = useState([])
  const [stepNotes, setStepNotes] = useState({}) // { stepIndex: "note text" }
  const [stepsLoaded, setStepsLoaded] = useState(false)
  const isEmployed = profile?.situation === 'employed'
  const taskData = task[isEmployed ? 'pathB' : 'pathA'] || task.pathA
  const steps = taskData?.primary?.steps || []
  const allDone = steps.length > 0 && completedSteps.length === steps.length

  // Load persisted step completion + notes for this day
  useEffect(() => {
    let cancelled = false
    setStepsLoaded(false)
    async function load() {
      if (!session?.user?.id || day == null) { setCompletedSteps([]); setStepNotes({}); setStepsLoaded(true); return }
      const { data } = await supabase
        .from('daily_progress')
        .select('completed_steps, step_notes')
        .eq('user_id', session.user.id)
        .eq('day_number', day)
        .maybeSingle()
      if (!cancelled) {
        setCompletedSteps(data?.completed_steps || [])
        setStepNotes(data?.step_notes || {})
        setStepsLoaded(true)
      }
    }
    load()
    return () => { cancelled = true }
  }, [session?.user?.id, day])

  async function persist(nextCompleted, nextNotes) {
    if (!session?.user?.id || day == null) return
    await supabase.from('daily_progress').upsert({
      user_id: session.user.id,
      day_number: day,
      completed_steps: nextCompleted,
      step_notes: nextNotes,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,day_number' })
  }

  async function toggleStep(i) {
    const done = completedSteps.includes(i)
    const next = done ? completedSteps.filter(x => x !== i) : [...completedSteps, i]
    setCompletedSteps(next)
    await persist(next, stepNotes)
  }

  async function saveStepNote(i, text) {
    const next = { ...stepNotes, [i]: text }
    setStepNotes(next)
    await persist(completedSteps, next)
  }
  
  // Book prompts for specific days
  const DAY_PROMPTS = {
    8: [`Give me 25 companies in [YOUR INDUSTRY] in [YOUR GEOGRAPHY] that hire [YOUR TARGET TITLE]. For each include: company size, recent growth signals, one reason it's worth targeting, and whether they're currently hiring.`],
    9: [
      `I am considering applying for the following role. Tell me honestly whether I meet 60-70% of the qualifications and whether I should apply.

JOB DESCRIPTION: [PASTE JOB DESCRIPTION]

MY RESUME: [PASTE RESUME]`,
      `What is the typical salary range for a [JOB TITLE] with [X YEARS] of experience at a [COMPANY SIZE] company in [GEOGRAPHY]? Include base salary range, typical bonus, and any equity components.`,
    ],
    11: [
      `What are the most used job boards for [YOUR SPECIFIC ROLE] in [YOUR INDUSTRY]? Include niche, regional, and industry-specific boards that might not appear on a general search.`,
      `What are the top staffing agencies that place [YOUR TITLE] roles in [YOUR FIELD AND GEOGRAPHY]?`,
    ],
    12: [`I am a [YOUR PROFESSION]. Give me a list of 2-5 networking events or professional groups within 30 minutes of [YOUR CITY] that could be relevant for growing my career. Include professional associations, industry meetups, civic organizations, and alumni groups.`],
    17: [`I applied for [ROLE] at [COMPANY] [X WEEKS] ago and have not heard back. Please draft a follow-up email that is warm, professional, and adds a brief new value point rather than just asking for a status update. Keep it under 100 words.`],
    21: [`I have been in a job search for [X WEEKS]. Here is my current pipeline: [PASTE PIPELINE SUMMARY]. I have not heard back from [X COMPANIES] in over two weeks. What should I prioritize this week and why?`],
    28: [`I am preparing for an interview for a [TARGET ROLE]. Please help me structure a STAR story from this experience: [DESCRIBE WHAT HAPPENED IN YOUR OWN WORDS]. Keep the result specific and measurable. I want to tell this in 90 seconds.`],
    35: [`I have been job searching for [X WEEKS] and I'm feeling discouraged. Here is where my search stands: [DESCRIBE YOUR CURRENT SITUATION]. What would you recommend I focus on this week to regain momentum?`],
    42: [`I have an interview for [ROLE] at [COMPANY] in [X DAYS]. Here is the job description: [PASTE JD]. Here is my resume: [PASTE RESUME]. Please give me: the 5 most likely interview questions, suggested answers for each, and 5 strong questions I should ask them.`],
    49: [`I am evaluating a job offer from [COMPANY NAME]. Please help me assess their company health: recent news including layoffs, leadership changes, funding; Glassdoor rating trends; financial health signals; and any recent executive departures.`],
    56: [`I have received a job offer from [COMPANY]. Base salary: [AMOUNT]. Market rate for this role: [YOUR RESEARCH]. My target: [AMOUNT]. Please draft a warm, professional negotiation response that expresses genuine enthusiasm while making a specific counter-offer. Under 150 words.`],
  }
  
  const prompts = (DAY_PROMPTS[day] || []).map(p => fillPlaceholders(p, profile))

  return (
    <div style={{ animation: 'slideUp 0.3s ease' }}>
      {taskData?.primary?.stealth && isEmployed && (
        <div style={{ background: '#fef3c7', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.78rem', color: '#92400e', lineHeight: 1.5 }}>
          🤫 <strong>Stealth note:</strong> {taskData.primary.stealth}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
        {steps.map((step, i) => {
          const done = completedSteps.includes(i)
          return (
            <div key={i} style={{ border: `2px solid ${done ? '#00712D' : '#e8e8e8'}`, borderRadius: '6px', background: done ? '#E8F2EC' : '#fff', transition: 'all 0.15s' }}>
              <div onClick={() => toggleStep(i)} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem', padding: '0.8rem', cursor: 'pointer' }}>
                <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${done ? '#00712D' : '#ccc'}`, background: done ? '#00712D' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '0.1rem' }}>
                  {done && <span style={{ color: '#fff', fontSize: '0.65rem', fontWeight: 700 }}>✓</span>}
                </div>
                <span style={{ fontSize: '0.875rem', color: done ? '#888' : '#1A1A1A', textDecoration: done ? 'line-through' : 'none', lineHeight: 1.5 }}>{step}</span>
              </div>
              <StepNote value={stepNotes[i] || ''} onSave={text => saveStepNote(i, text)} />
            </div>
          )
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ flex: 1, height: '4px', background: '#e8e8e8', borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${steps.length ? (completedSteps.length / steps.length) * 100 : 0}%`, background: '#00712D', transition: 'width 0.3s' }} />
        </div>
        <span style={{ fontSize: '0.72rem', color: '#888' }}>{completedSteps.length}/{steps.length}</span>
      </div>

      {/* Book prompts for this day */}
      {prompts.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
            AI Prompts from the book
          </div>
          {prompts.map((prompt, i) => (
            <PromptBlock key={i} label={`Prompt ${prompts.length > 1 ? i + 1 : ''} — copy and paste into Claude.ai or ChatGPT`} prompt={prompt} session={session} day={day} promptIndex={i} />
          ))}
        </div>
      )}

      {allDone && (
        <button className="btn-primary" onClick={onComplete} style={{ width: '100%', padding: '0.9rem' }}>
          ✓ Day complete →
        </button>
      )}
    </div>
  )
}

// ─── MAIN DAILY WORKBOOK ─────────────────────────────────────────────
export default function DailyWorkbook({ session, profile, currentDay, activeDays, onDayComplete, onStatsUpdate, curriculum, initialDay, onBack }) {
  const [showCelebration, setShowCelebration] = useState(false)
  const [completedDays, setCompletedDays] = useState([])
  const [viewingDay, setViewingDay] = useState(initialDay || currentDay)
  const [loaded, setLoaded] = useState(false)
  const [justCompletedDay, setJustCompletedDay] = useState(null)

  // Load completed days from database and set viewing day to next uncompleted
  useEffect(() => {
    async function loadCompleted() {
      const { supabase } = await import('../supabase')
      const { data } = await supabase.from('daily_completions').select('day_number').eq('user_id', session.user.id)
      if (data) {
        const done = data.map(d => d.day_number).filter(Boolean)
        setCompletedDays(done)
        // Jump to a specific day if requested (e.g. editing Day 1 from My Search), otherwise the next uncompleted day
        setViewingDay(initialDay || currentDay)
      }
      setLoaded(true)
    }
    loadCompleted()
  }, [currentDay])

  // Available days: all days up to and including currentDay
  const availableDays = Object.keys(curriculum).map(Number).filter(d => d <= Math.max(currentDay, 1)).sort((a, b) => a - b)

  const task = curriculum[viewingDay] || curriculum[availableDays[availableDays.length - 1]]
  const isEmployed = profile?.situation === 'employed'
  const mantra = getMantra(activeDays)
  const dayLabel = viewingDay > 60 ? 'Today' : viewingDay === currentDay ? 'Today' : `Day ${viewingDay}`
  const isDayComplete = completedDays.includes(viewingDay)

  async function handleComplete() {
    setJustCompletedDay(viewingDay)
    setShowCelebration(true)
    await onDayComplete(viewingDay)
    setCompletedDays(prev => [...new Set([...prev, viewingDay])])
  }

  function renderExercise() {
    if (viewingDay === 1) return <Day1Exercise session={session} profile={profile} onComplete={handleComplete} />
    if (viewingDay === 2) return <Day2Exercise session={session} profile={profile} onComplete={handleComplete} />
    if (viewingDay === 3) return <Day3Exercise session={session} profile={profile} onComplete={handleComplete} />
    if (viewingDay === 4) return <Day4Exercise session={session} profile={profile} onComplete={handleComplete} />
    if (viewingDay === 5) return <Day5Exercise session={session} profile={profile} onComplete={handleComplete} onStatsUpdate={onStatsUpdate} />
    if (viewingDay === 8) return <Day8Exercise session={session} profile={profile} onComplete={handleComplete} />
    return <ActionDay day={viewingDay} task={task} profile={profile} session={session} onComplete={handleComplete} />
  }

  if (showCelebration) return (
    <div className="card" style={{ textAlign: 'center', padding: '2.5rem 2rem', borderTop: '4px solid #00712D', animation: 'pop 0.3s ease' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
      <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.2rem', fontWeight: 700, color: '#00712D', marginBottom: '0.75rem' }}>
        Day {justCompletedDay || viewingDay} complete.
      </div>
      <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.65, fontStyle: 'italic', marginBottom: '1.5rem' }}>
        "{task?.celebrate || 'Keep going. The system is working.'}"
      </p>
      {currentDay >= 60 && (
        <div style={{ marginBottom: '1rem' }}>
          <CalendarExport session={session} />
        </div>
      )}
      <button className="btn-primary" onClick={() => { 
        const nextDay = (justCompletedDay || viewingDay) + 1
        setShowCelebration(false)
        if (nextDay <= 60) setViewingDay(nextDay)
      }} style={{ width: '100%' }}>
        {(justCompletedDay || viewingDay) < 60 ? `Start Day ${(justCompletedDay || viewingDay) + 1} →` : 'Continue your search →'}
      </button>
    </div>
  )

  return (
    <div>
      {onBack && (
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#00712D', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          ← Back to My Search
        </button>
      )}
      {/* Day navigation */}
      {availableDays.length > 1 && (
        <div style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', marginBottom: '1rem', paddingBottom: '0.25rem' }}>
          {availableDays.map(d => {
            const done = completedDays.includes(d)
            const active = viewingDay === d
            return (
              <button key={d} onClick={() => { setViewingDay(d); setShowCelebration(false) }} style={{ padding: '0.35rem 0.65rem', border: `2px solid ${active ? '#00712D' : done ? '#00712D' : '#e8e8e8'}`, borderRadius: '6px', background: active ? '#00712D' : done ? '#E8F2EC' : '#fff', color: active ? '#fff' : done ? '#00712D' : '#888', fontSize: '0.72rem', fontWeight: active ? 700 : done ? 600 : 400, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                {done && !active ? '✓ ' : ''}Day {d}
              </button>
            )
          })}
        </div>
      )}

      {/* Mantra */}
      <div style={{ background: '#00712D', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#fff', lineHeight: 1.6, fontStyle: 'italic' }}>
        "{mantra}"
      </div>

      {/* Day header */}
      <div style={{ marginBottom: '1rem' }}>
        <div className="eyebrow">{dayLabel}{isDayComplete ? ' · ✓ Complete' : ''}{task?.lesson?.chapter ? ` · ${task.lesson.chapter}` : ''}</div>
        <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: 700, color: '#1A1A1A', lineHeight: 1.3 }}>
          {task?.[isEmployed ? 'pathB' : 'pathA']?.primary?.title || task?.pathA?.primary?.title || 'Today'}
        </h2>
        {(isEmployed) && <span className="badge badge-gold" style={{ marginTop: '0.35rem', display: 'inline-block' }}>🤫 Stealth Search</span>}
        {(!isEmployed) && <span className="badge badge-green" style={{ marginTop: '0.35rem', display: 'inline-block' }}>🔍 Full-Time Search</span>}
      </div>

      {/* Lesson */}
      {task?.lesson && <LessonCard lesson={task.lesson} />}

      {/* The exercise */}
      {renderExercise()}
    </div>
  )
}
